import { memo, useCallback, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { TQuestionForm } from "./QuestionSchema";
import { Information, useFetch, useFormContext } from "@4uhub/lib4uhub";
import RadioGroupInput from "../../../../components/RadioGroup/RadioGroupInput";
import { Box, Grid } from "@mui/material";
import { listAlternatives } from "../../../../services/question.service";
import { IQuestionAlternatives } from "../../../../models/questions";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IFreeAlternative,
  QuestionAlternativesFree,
} from "./QuestionAlternativesFree";

const translationPath = "page.assessments.questions.";

export const QuestionAlternatives = memo(() => {
  const { t } = useTranslation();

  const [questionAlternatives, setQuestionAlternatives] = useState<
    IQuestionAlternatives[]
  >([]);

  const [freeAlternatives, setFreeAlternatives] = useState<IFreeAlternative[]>(
    [],
  );

  const { id } = useParams();

  const { getValues, setValue, control } = useFormContext<TQuestionForm>();

  const evaluationType = useWatch({ name: "questionEvaluationType", control });

  const { sendRequest } = useFetch(listAlternatives);

  const code = evaluationType?.code ?? "";

  const isSimNao = code === "6";
  const isSimplesEscolha = code === "11";
  const showTasyIntegrationHint = ["1", "7", "8", "9", "10"].includes(code);

  const fetchAlternatives = useCallback(async () => {
    const { data, success } = await sendRequest({
      QuestionEvaluationTypeId: evaluationType ? evaluationType.id : "",
    });
    if (data && success) {
      setQuestionAlternatives(data);
    }
  }, [sendRequest, evaluationType]);

  useEffect(() => {
    setQuestionAlternatives([]);

    if (isSimNao) {
      return;
    }

    if (isSimplesEscolha) {
      if (id) {
        const currentAlternatives = getValues("questionAlternatives");
        if (currentAlternatives && currentAlternatives.length > 0) {
          setFreeAlternatives(
            currentAlternatives.map((alt) => ({
              id: alt.id ? alt.id : null,
              label: alt.label ?? "",
              nrOption: alt.nrOption,
              integrationId: alt.integrationId ?? "",
            })),
          );
        } else {
          setFreeAlternatives([
            { id: null, label: "", nrOption: 1, integrationId: "" },
          ]);
        }
      } else {
        setFreeAlternatives((prev) =>
          prev.length === 0
            ? [{ id: null, label: "", nrOption: 1, integrationId: "" }]
            : prev,
        );
      }
      return;
    }

    if (evaluationType) {
      fetchAlternatives();
    }
  }, [
    id,
    code,
    evaluationType,
    isSimNao,
    isSimplesEscolha,
    fetchAlternatives,
    getValues,
  ]);

  const handleFreeAlternativesChange = useCallback(
    (alts: IFreeAlternative[]) => {
      setFreeAlternatives(alts);
      setValue(
        "questionAlternatives",
        alts.map((a, i) => ({
          id: a.id,
          questionEvaluationTypeAlternativeId: null,
          nrOption: a.nrOption,
          displayOrder: i + 1,
          integrationId: a.integrationId ?? null,
          label: a.label,
        })),
      );
    },
    [setValue],
  );

  if (isSimplesEscolha) {
    return (
      <Grid item sm={12}>
        <QuestionAlternativesFree
          value={freeAlternatives}
          onChange={handleFreeAlternativesChange}
        />
      </Grid>
    );
  }

  if (isSimNao) {
    return null;
  }

  return (
    <Grid item sm={12}>
      {questionAlternatives.length !== 0 && (
        <Information
          info={t(translationPath + "alternatives_integration_id_tooltip")}
          showInformation={showTasyIntegrationHint}
        >
          <Box flex={1} minWidth={0}>
            <RadioGroupInput
              name={"questionAlternatives"}
              groupLabel={"Alternativas"}
              disableRadios
              radios={questionAlternatives
                .sort((a, b) =>
                  a.nrOption > b.nrOption ? 1 : a.nrOption < b.nrOption ? -1 : 0,
                )
                .map((alternative) => {
                  const displayLabel = alternative.label;
                  return {
                    label: displayLabel
                      ? `${alternative.nrOption} - ${displayLabel}`
                      : alternative.nrOption.toString(),
                    value: alternative.nrOption.toString(),
                  };
                })}
            />
          </Box>
        </Information>
      )}
    </Grid>
  );
});
